import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AdminUsers from './pages/AdminUsers';
import FloodDashboard from './pages/FloodDashboard';
import PropertyEvaluation from './pages/PropertyEvaluation';
import FmdDashboard from './pages/FmdDashboard';
import ReportLivestock from './pages/ReportLivestock';
import Layout from './components/Layout';
import './App.css';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading Geoventory...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Admin Route component - requires administrator role
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading Geoventory...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.role !== 'administrator') {
    return <Navigate to="/" />;
  }

  return children;
};

// Login Route - redirect if already logged in
const LoginRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading Geoventory...</p>
      </div>
    );
  }

  // If user is already logged in (including dev mode), redirect to dashboard
  if (user) {
    return <Navigate to="/" />;
  }

  return children;
};

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="App">
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <Routes>
          <Route
            path="/login"
            element={
              <LoginRoute>
                <Login />
              </LoginRoute>
            }
          />
          {/* PUBLIC — farmers report sick livestock without an account */}
          <Route path="/report" element={<ReportLivestock />} />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <Layout>
                  <AdminUsers />
                </Layout>
              </AdminRoute>
            }
          />
          <Route
            path="/floods"
            element={
              <ProtectedRoute>
                <Layout>
                  <FloodDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/property"
            element={
              <ProtectedRoute>
                <Layout>
                  <PropertyEvaluation />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/fmd"
            element={
              <ProtectedRoute>
                <Layout>
                  <FmdDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
